/**
 * Building blocks shared by the Neusix Sales Console pages.
 *
 * Presentation only. A missing value renders as "Not provided" in a muted
 * style, so a gap is visible and never mistaken for data.
 */

import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { clientUrl, LIFECYCLE, NOT_PROVIDED } from './consoleData';

/**
 * Copy one client's existing assessment link to the clipboard.
 *
 * For the case the console could not answer before: a client lost their link
 * and asks for it again. This hands back the link that was issued when the
 * session was created — the same token, resolving to the same conversation,
 * with everything already discovered still in it. Nothing is created, no new
 * token is minted, and no request is sent; the link is already in the session
 * record the page is rendering.
 */
export function CopyLinkButton({ path, label = 'Copy link' }) {
  const [copied, setCopied] = useState(false);
  const url = clientUrl(path);
  if (!url) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <button
      type="button"
      className="nsc-link nsc-link--button"
      onClick={copy}
      title={url}
    >
      {copied ? 'Copied' : label}
    </button>
  );
}

export function PageHeader({ back, eyebrow, title, subtitle, actions, children }) {
  return (
    <header className="nsc-page-header">
      {back && (
        <Link to={back.to} className="nsc-back">
          <span aria-hidden="true">←</span> {back.label}
        </Link>
      )}
      <div className="nsc-page-header__row">
        <div className="nsc-page-header__text">
          {eyebrow && <p className="nsc-eyebrow">{eyebrow}</p>}
          <h1 className="nsc-h1">{title}</h1>
          {subtitle && <p className="nsc-subtitle">{subtitle}</p>}
          {children}
        </div>
        {actions && <div className="nsc-page-header__actions">{actions}</div>}
      </div>
    </header>
  );
}

export function Card({ title, description, actions, children, className = '' }) {
  const headingId = useId();
  return (
    <section
      className={`nsc-card ${className}`.trim()}
      aria-labelledby={title ? headingId : undefined}
    >
      {(title || actions) && (
        <div className="nsc-card__header">
          <div className="nsc-card__heading">
            {title && <h2 id={headingId} className="nsc-card__title">{title}</h2>}
            {description && <p className="nsc-card__description">{description}</p>}
          </div>
          {actions && <div className="nsc-card__actions">{actions}</div>}
        </div>
      )}
      <div className="nsc-card__body">{children}</div>
    </section>
  );
}

export function Badge({ tone = 'neutral', children, title }) {
  return (
    <span className={`nsc-badge nsc-badge--${tone}`} title={title}>
      {children}
    </span>
  );
}

export function StatusBadge({ lifecycle }) {
  const known = LIFECYCLE[lifecycle] || { label: lifecycle || 'Unknown', tone: 'neutral' };
  return (
    <Badge tone={known.tone}>
      <span className="nsc-badge__dot" aria-hidden="true" />
      {known.label}
    </Badge>
  );
}

export function LoadingState({ label = 'Loading…' }) {
  return (
    <div className="nsc-state" role="status">
      <span className="nsc-spinner" aria-hidden="true" />
      <p className="nsc-state__body">{label}</p>
    </div>
  );
}

export function EmptyState({ title, children, action, compact = false }) {
  return (
    <div className={`nsc-state${compact ? ' nsc-state--compact' : ''}`}>
      {title && <p className="nsc-state__title">{title}</p>}
      {children && <p className="nsc-state__body">{children}</p>}
      {action && <div className="nsc-state__action">{action}</div>}
    </div>
  );
}

export function ErrorState({ title = 'Something went wrong', children, action }) {
  return (
    <div className="nsc-state nsc-state--error" role="alert">
      <p className="nsc-state__title">{title}</p>
      {children && <p className="nsc-state__body">{children}</p>}
      {action && <div className="nsc-state__action">{action}</div>}
    </div>
  );
}

function isEmpty(value) {
  return value === null || value === undefined || value === '' || value === NOT_PROVIDED;
}

/** A value, or a muted "Not provided". */
export function Value({ value }) {
  if (isEmpty(value)) return <span className="nsc-empty-value">{NOT_PROVIDED}</span>;
  return value;
}

/** Label/value pairs. Each item: { label, value, tag? }. */
export function Facts({ items, columns = 2 }) {
  return (
    <dl className={`nsc-facts nsc-facts--${columns}`}>
      {items.map((item) => (
        <div key={item.label} className="nsc-facts__row">
          <dt className="nsc-facts__label">{item.label}</dt>
          <dd className="nsc-facts__value">
            <Value value={item.value} />
            {item.tag}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function BulletList({ items, empty = NOT_PROVIDED }) {
  if (!items || items.length === 0) return <p className="nsc-empty-value">{empty}</p>;
  return (
    <ul className="nsc-bullets">
      {items.map((item, index) => <li key={index}>{item}</li>)}
    </ul>
  );
}

export function Prose({ text, empty = NOT_PROVIDED }) {
  if (isEmpty(text)) return <p className="nsc-empty-value">{empty}</p>;
  return <p className="nsc-prose">{text}</p>;
}

export function SubHeading({ children }) {
  return <h3 className="nsc-h3">{children}</h3>;
}
